import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, Plus, Minus, Star, Truck, Shield, RotateCcw, MessageCircle, Lock, ShoppingBag } from "lucide-react";
import { api, formatINR, whatsappLink } from "../lib/api";
import { useShop } from "../context/ShopContext";
import ProductCard from "../components/ProductCard";
import JsonLd, { SITE_URL } from "../components/JsonLd";
import QuickOrderModal from "../components/QuickOrderModal";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toggleWishlist, isWishlisted, addToCart } = useShop();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [imageIdx, setImageIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("description");
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, title: "", body: "" });
  const [modalOpen, setModalOpen] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyPhone, setNotifyPhone] = useState("");
  const [notifySubmitting, setNotifySubmitting] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);
  const ctaRef = useRef(null);

  useEffect(() => {
    if (!product) return;
    const el = ctaRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [product]);

  useEffect(() => {
    api.get(`/products/slug/${slug}`).then((r) => {
      setProduct(r.data);
      setImageIdx(0);
      api.get("/products", { params: { category: r.data.category, limit: 8 } }).then((rr) => {
        setRelated(rr.data.filter((p) => p.id !== r.data.id).slice(0, 4));
      });
      api.get(`/reviews/${r.data.id}`).then((rev) => setReviews(rev.data));
    }).catch(() => navigate("/shop"));
  }, [slug, navigate]);

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", { ...reviewForm, product_id: product.id });
      const r = await api.get(`/reviews/${product.id}`);
      setReviews(r.data);
      setReviewForm({ name: "", rating: 5, title: "", body: "" });
      toast.success("Review submitted");
    } catch { toast.error("Failed to submit review"); }
  };

  const submitNotifyMe = async (e) => {
    e.preventDefault();
    setNotifySubmitting(true);
    try {
      await api.post(`/products/${product.id}/notify-me`, { email: notifyEmail, phone: notifyPhone || null });
      setNotifyDone(true);
      toast.success("We'll email you the moment it's back in stock.");
    } catch {
      toast.error("Could not save your request. Try again.");
    } finally { setNotifySubmitting(false); }
  };

  if (!product) return <div className="min-h-[60vh] flex items-center justify-center text-white/60">Loading...</div>;

  const wished = isWishlisted(product.id);
  const discount = product.compare_price ? Math.round(((product.compare_price - product.price) / product.compare_price) * 100) : 0;
  const outOfStock = product.stock <= 0;

  // Product JSON-LD for Google rich results
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.description,
    image: (product.images || []).map((img) => img.startsWith("http") ? img : `${SITE_URL}${img}`),
    sku: product.id,
    brand: { "@type": "Brand", name: product.brand || "MileForge" },
    aggregateRating: product.review_count > 0 ? {
      "@type": "AggregateRating",
      ratingValue: product.rating,
      reviewCount: product.review_count,
    } : undefined,
    offers: {
      "@type": "Offer",
      url: `${SITE_URL}/product/${product.slug}`,
      priceCurrency: "INR",
      price: product.price,
      availability: product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Shop", item: `${SITE_URL}/shop` },
      { "@type": "ListItem", position: 3, name: product.subcategory, item: `${SITE_URL}/shop?category=${product.category}` },
      { "@type": "ListItem", position: 4, name: product.name },
    ],
  };

  return (
    <div data-testid="product-detail-page" className="min-h-screen">
      <JsonLd data={productSchema} />
      <JsonLd data={breadcrumbSchema} />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8 pb-24">
        {/* Breadcrumb */}
        <nav className="text-xs font-body text-white/50 mb-6">
          <Link to="/" className="hover:text-white">Home</Link> / <Link to="/shop" className="hover:text-white">Shop</Link> / <Link to={`/shop?category=${product.category}`} className="hover:text-white">{product.category}</Link> / <span className="text-white/80">{product.name}</span>
        </nav>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* Gallery */}
          <div className="lg:col-span-7">
            <div className="aspect-[4/3] sm:aspect-square bg-[#111] overflow-hidden mb-4 relative group">
              <img
                data-testid="product-main-image"
                src={product.images[imageIdx]}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {discount > 0 && (
                <div className="absolute top-4 left-4 bg-[#D96A1B] text-white font-display tracking-widest text-sm px-3 py-1.5">-{discount}%</div>
              )}
            </div>
            {product.images.length > 1 && (
              <div className="grid grid-cols-5 gap-3">
                {product.images.map((src, i) => (
                  <button
                    key={src}
                    data-testid={`thumb-${i}`}
                    onClick={() => setImageIdx(i)}
                    className={`aspect-square overflow-hidden border ${i === imageIdx ? "border-[#D96A1B]" : "border-white/10"}`}
                  >
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <div className="overline mb-2">{product.subcategory}</div>
            <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tight">{product.name}</h1>

            <div className="mt-3 flex items-center gap-3">
              <div className="flex">
                {Array(5).fill(0).map((_, i) => (
                  <Star key={i} size={16} fill={i < Math.round(product.rating) ? "#D96A1B" : "transparent"} className={i < Math.round(product.rating) ? "text-[#D96A1B]" : "text-white/30"} />
                ))}
              </div>
              <span className="text-sm text-white/60 font-body">{product.rating} ({product.review_count} reviews)</span>
            </div>

            <div className="mt-5 flex items-baseline gap-3">
              <span className="font-display text-4xl text-[#D96A1B]">{formatINR(product.price)}</span>
              {product.compare_price && (
                <>
                  <span className="text-lg text-white/40 line-through font-body">{formatINR(product.compare_price)}</span>
                  <span className="text-xs bg-[#D96A1B]/20 text-[#D96A1B] font-display px-2 py-1">SAVE {discount}%</span>
                </>
              )}
            </div>
            <p className="text-xs text-white/50 mt-1 font-body">Inclusive of all taxes. Free shipping above ₹999.</p>

            {/* Qty + Actions */}
            <div ref={ctaRef}>
              {outOfStock ? (
                <div data-testid="pdp-sold-out-block" className="mt-7 border border-white/10 p-5">
                  <div className="flex items-center gap-3">
                    <span data-testid="pdp-sold-out-badge" className="bg-white/10 text-white/70 font-display tracking-widest text-xs px-3 py-1.5 uppercase">Sold Out</span>
                    <button
                      data-testid="pdp-wishlist"
                      onClick={() => toggleWishlist(product)}
                      className={`btn-ghost px-4 py-2 ${wished ? "text-[#D96A1B] border-[#D96A1B]" : ""}`}
                      aria-label="Wishlist"
                    ><Heart size={16} fill={wished ? "#D96A1B" : "none"} /></button>
                  </div>
                  <p className="mt-4 text-sm text-white/70 font-body">This one's out of stock right now. Leave your email (and phone, optional) and we'll let you know the moment fresh stock arrives.</p>
                  {notifyDone ? (
                    <p data-testid="pdp-notify-success" className="mt-4 text-sm text-[#D96A1B] font-display uppercase tracking-wide">You're on the list — we'll email you when it's back.</p>
                  ) : (
                    <form onSubmit={submitNotifyMe} className="mt-4 flex flex-col sm:flex-row gap-3">
                      <input
                        data-testid="pdp-notify-email"
                        type="email" required
                        value={notifyEmail}
                        onChange={(e) => setNotifyEmail(e.target.value)}
                        placeholder="you@email.com"
                        className="flex-1 bg-[#111] border border-white/15 px-4 py-3 min-h-[44px] focus:outline-none focus:border-[#D96A1B] font-body"
                      />
                      <input
                        data-testid="pdp-notify-phone"
                        type="tel"
                        value={notifyPhone}
                        onChange={(e) => setNotifyPhone(e.target.value)}
                        placeholder="Phone (optional)"
                        className="sm:w-40 bg-[#111] border border-white/15 px-4 py-3 min-h-[44px] focus:outline-none focus:border-[#D96A1B] font-body"
                      />
                      <button data-testid="pdp-notify-submit" disabled={notifySubmitting} className="btn-orange justify-center min-h-[44px]">
                        {notifySubmitting ? "Sending..." : "Notify Me"}
                      </button>
                    </form>
                  )}
                </div>
              ) : (
                <>
                  <div className="mt-7 flex items-center gap-4">
                    <div className="flex items-center border border-white/20">
                      <button data-testid="pdp-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-white/5"><Minus size={14} /></button>
                      <span data-testid="pdp-qty" className="px-5 font-display">{qty}</span>
                      <button data-testid="pdp-qty-plus" onClick={() => setQty(qty + 1)} className="p-3 hover:bg-white/5"><Plus size={14} /></button>
                    </div>
                    <button
                      data-testid="pdp-buy-now"
                      onClick={() => setModalOpen(true)}
                      className="btn-orange flex-1 justify-center"
                    ><Lock size={16} /> Buy Now · Secure Pay</button>
                  </div>
                  <div className="mt-3 flex gap-3">
                    <button
                      data-testid="pdp-add-to-cart"
                      onClick={() => addToCart(product, qty)}
                      className="btn-ghost flex-1 justify-center"
                    ><ShoppingBag size={16} /> Add to Cart</button>
                    <button
                      data-testid="pdp-wishlist"
                      onClick={() => toggleWishlist(product)}
                      className={`btn-ghost px-4 ${wished ? "text-[#D96A1B] border-[#D96A1B]" : ""}`}
                      aria-label="Wishlist"
                    ><Heart size={16} fill={wished ? "#D96A1B" : "none"} /></button>
                  </div>
                  <p data-testid="pdp-payment-note" className="mt-3 text-[11px] text-white/50 font-body leading-relaxed">
                    Fill in your ship-to address, then complete payment securely via Cashfree (UPI, Cards, Netbanking). Your order is <span className="text-[#D96A1B]">reserved for 24 hours</span>.
                  </p>
                </>
              )}
            </div>

            {/* Trust badges */}
            <div className="mt-8 grid grid-cols-3 gap-3 border-t border-white/10 pt-6">
              {[{ Icon: Truck, t: "Free Shipping" }, { Icon: Shield, t: "Secure Checkout" }, { Icon: RotateCcw, t: "7-Day Returns" }].map((b) => (
                <div key={b.t} className="text-center">
                  <b.Icon size={20} className="text-[#D96A1B] mx-auto" />
                  <div className="mt-2 text-[10px] tracking-widest uppercase font-display text-white/60">{b.t}</div>
                </div>
              ))}
            </div>

            <a
              href={whatsappLink(`Hi MileForge, I have a question about ${product.name}`)}
              target="_blank" rel="noreferrer"
              data-testid="pdp-whatsapp"
              className="mt-6 flex items-center gap-2 text-sm text-[#25D366] hover:text-[#1ebe5b] font-display tracking-widest uppercase"
            >
              <MessageCircle size={16} /> Ask on WhatsApp
            </a>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-20">
          <div className="flex gap-6 border-b border-white/10">
            {["description", "features", "specifications", "reviews"].map((t) => (
              <button
                key={t}
                data-testid={`tab-${t}`}
                onClick={() => setTab(t)}
                className={`pb-3 font-display tracking-widest uppercase text-sm transition-colors ${tab === t ? "text-[#D96A1B] border-b-2 border-[#D96A1B]" : "text-white/60 hover:text-white"}`}
              >{t}</button>
            ))}
          </div>

          <div className="py-8 font-body text-white/75 leading-relaxed">
            {tab === "description" && <p className="max-w-3xl">{product.description}</p>}
            {tab === "features" && (
              <ul className="space-y-2 max-w-2xl">
                {product.features.map((f) => <li key={f} className="flex gap-3"><span className="text-[#D96A1B]">▸</span>{f}</li>)}
              </ul>
            )}
            {tab === "specifications" && (
              <table className="w-full max-w-2xl">
                <tbody>
                  {Object.entries(product.specifications).map(([k, v]) => (
                    <tr key={k} className="border-b border-white/10">
                      <td className="py-3 text-xs uppercase tracking-widest font-display text-white/50 w-1/3">{k}</td>
                      <td className="py-3 text-sm">{String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {tab === "reviews" && (
              <div className="grid md:grid-cols-2 gap-10 max-w-5xl">
                <div className="space-y-5">
                  {reviews.length === 0 && <p className="text-white/50">No reviews yet. Be the first.</p>}
                  {reviews.map((r) => (
                    <div key={r.id} className="border border-white/10 p-5">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-display uppercase text-sm tracking-widest">{r.title}</div>
                          <div className="text-xs text-white/50 mt-1">{r.name}</div>
                        </div>
                        <div className="flex">
                          {Array(5).fill(0).map((_, i) => (
                            <Star key={i} size={12} fill={i < r.rating ? "#D96A1B" : "transparent"} className={i < r.rating ? "text-[#D96A1B]" : "text-white/30"} />
                          ))}
                        </div>
                      </div>
                      <p className="mt-3 text-sm text-white/70">{r.body}</p>
                    </div>
                  ))}
                </div>
                <form onSubmit={submitReview} className="border border-white/10 p-6 h-fit">
                  <h3 className="font-display uppercase tracking-widest text-sm mb-5">Write A Review</h3>
                  <input data-testid="review-name" required value={reviewForm.name} onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })} placeholder="Your Name" className="w-full bg-[#111] border border-white/15 px-3 py-2.5 mb-3 focus:outline-none focus:border-[#D96A1B]" />
                  <select data-testid="review-rating" value={reviewForm.rating} onChange={(e) => setReviewForm({ ...reviewForm, rating: Number(e.target.value) })} className="w-full bg-[#111] border border-white/15 px-3 py-2.5 mb-3 focus:outline-none focus:border-[#D96A1B]">
                    {[5,4,3,2,1].map((n) => <option key={n} value={n}>{n} Stars</option>)}
                  </select>
                  <input data-testid="review-title" required value={reviewForm.title} onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })} placeholder="Review Title" className="w-full bg-[#111] border border-white/15 px-3 py-2.5 mb-3 focus:outline-none focus:border-[#D96A1B]" />
                  <textarea data-testid="review-body" required value={reviewForm.body} onChange={(e) => setReviewForm({ ...reviewForm, body: e.target.value })} placeholder="Your experience..." rows="4" className="w-full bg-[#111] border border-white/15 px-3 py-2.5 mb-3 focus:outline-none focus:border-[#D96A1B]" />
                  <button data-testid="submit-review" className="btn-orange w-full justify-center">Submit Review</button>
                </form>
              </div>
            )}
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-20">
            <h2 className="section-title text-3xl mb-10">You May Also Like</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        )}
      </div>

      {/* Sticky mobile bar — fades in only once the inline CTA scrolls out of view */}
      <div
        data-testid="pdp-sticky-mobile-bar"
        className={`lg:hidden fixed bottom-0 left-0 right-0 bg-[#0A0A0A] border-t border-white/10 p-3 z-30 flex gap-2 transition-all duration-300 ${
          showStickyBar ? "translate-y-0 opacity-100" : "translate-y-full opacity-0 pointer-events-none"
        }`}
      >
        {outOfStock ? (
          <button
            data-testid="mobile-notify-me"
            onClick={() => ctaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
            className="btn-orange flex-1 justify-center py-3"
          >Notify Me When Back In Stock</button>
        ) : (
          <>
            <button onClick={() => toggleWishlist(product)} className="btn-ghost px-3.5 py-3"><Heart size={16} fill={wished ? "#D96A1B" : "none"} /></button>
            <button data-testid="mobile-add-to-cart" onClick={() => addToCart(product, qty)} className="btn-ghost px-3.5 py-3"><ShoppingBag size={16} /></button>
            <button data-testid="mobile-buy-now" onClick={() => setModalOpen(true)} className="btn-orange flex-1 justify-center py-3"><Lock size={14} /> Buy — {formatINR(product.price * qty)}</button>
          </>
        )}
      </div>

      <QuickOrderModal product={product} qty={qty} open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}
